import {Config, mapStoppage, isStoppageTypeWithReason} from '@domain/shifthandover'
import {getStoppageDuration} from '@domain/stoppages'
import {DateTimePeriod} from '@hconnect/common/components'
import {ProcessStageTitle} from '@hconnect/common/components/shiftEventLabels'
import {Tag} from '@hconnect/common/components/Tag'
import {MAX_ALLOWED_ATTACHMENTS, STOPPAGE_TYPE_TRANSLATION_KEYS} from '@hconnect/common/consts'
import {useKpiNodeIds} from '@hconnect/common/hooks'
import {useProcessStages} from '@hconnect/common/hooks/useProcessStages'
import {useSubmit} from '@hconnect/common/stoppages/hooks/useSubmit'
import {StoppageEvent} from '@hconnect/common/stoppages/types/stoppage.types'
import {headlineSx, imageBoxSx} from '@hconnect/common/styles'
import type {EquipmentData, HiddenFields} from '@hconnect/common/types'
import {getMaintainAnalysisUrl, getUniqueAttachments} from '@hconnect/common/utils'
import {GridSetting} from '@hconnect/uikit'
import {CardBox, CardSectionTitle} from '@hconnect/uikit/src/lib2'
import {Box, Divider, Grid, Link, Typography} from '@mui/material'
import {AttachmentItem, AttachmentItemsDisplay} from '@widgets/common-attachments'
import {EventTypeLabel, getEquipmentLabelDetails} from '@widgets/common-shift-event'
import React, {useState} from 'react'
import {useTranslation} from 'react-i18next'

import {SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE} from '../../consts'
import {getStoppageDescription} from '../../utils'
import {SalesImpactLabel} from '../eventProperties'
import {SubmitSection} from '../SubmitSection'

import {StoppageDetailsActions} from './StoppageDetailsActions'

type StoppageDetailsProps = {
  plantId: string
  timezone: string
  stoppage: StoppageEvent
  shifthandoverConfig?: Config
  isSplitStoppageEnabled?: boolean
  isEditStoppageEnabled?: boolean
  isDeleteStoppageEnabled?: boolean
  onEdit?: () => void
  onDelete?: () => void
  onClose?: () => void
  onSubmit?: () => void
  onSplit?: () => void
  hideSalesImpact?: boolean
  isStoppageSubmitSupported: boolean
  hiddenFields?: HiddenFields
  hideAttachments?: boolean
}

const regularSize: GridSetting = {
  xs: 12,
  sm: 4,
  md: 6
}

const renderProcessStage = (
  processStage: string | null | undefined,
  stages: Map<string, string> | undefined
) => {
  if (!processStage) return ' - '

  return <ProcessStageTitle stages={stages} stage={processStage} />
}

const renderTitle = (isHidden: boolean | undefined, title: string) => {
  if (isHidden) return null

  return (
    <Typography variant="h3" data-test-id="stoppage-details-title">
      {title}
    </Typography>
  )
}

const renderStoppageDescription = (
  isHidden: boolean | undefined,
  description: string | null,
  translate: (key: string) => string
) => {
  if (isHidden) return null

  return (
    <Grid item xs={12}>
      <Tag label={translate('shiftEvent.label.description')}>
        {description ?? translate('shiftEvent.action.noneOfThat')}
      </Tag>
    </Grid>
  )
}

const renderRootCausingEquipment = (
  hiddenFields: HiddenFields | undefined,
  translate: (key: string) => string,
  equipment?: EquipmentData | undefined,
  mainEquipment?: EquipmentData | undefined
) => {
  if (hiddenFields?.equipmentNumber && hiddenFields?.mainEquipment) return null

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h5" fontWeight={600} mt={1.5} mb={3.5}>
          {translate('shiftEvent.label.rootCausingEquipment')}
        </Typography>
        {!hiddenFields?.equipmentNumber && (
          <Tag
            data-test-id="event-detail-equipment"
            label={translate('shiftEvent.label.equipment')}
          >
            {getEquipmentLabelDetails(equipment)}
          </Tag>
        )}
      </Grid>
      <Grid item xs={12}>
        {!hiddenFields?.mainEquipment && (
          <Tag
            data-test-id="event-detail-main-equipment"
            label={translate('shiftEvent.label.mainEquipment')}
          >
            {getEquipmentLabelDetails(mainEquipment, false)}
          </Tag>
        )}
      </Grid>
    </>
  )
}

export const StoppageDetails = ({
  plantId,
  stoppage,
  timezone,
  shifthandoverConfig,
  isSplitStoppageEnabled = true,
  isEditStoppageEnabled = true,
  isDeleteStoppageEnabled = true,
  onEdit,
  onSplit,
  onSubmit,
  onDelete,
  onClose,
  hideSalesImpact,
  isStoppageSubmitSupported,
  hiddenFields,
  hideAttachments
}: StoppageDetailsProps) => {
  const {t} = useTranslation(SHIFT_EVENT_DETAILS_WIDGET_NAMESPACE)
  const departments = useKpiNodeIds(plantId)

  const [activeAttachmentId, setActiveAttachmentId] = useState<number | undefined>(undefined)

  const {data} = useProcessStages(plantId)
  const {isLoading: isSubmitting, submitStoppage} = useSubmit(plantId, onSubmit)

  const uniqueAttachments = getUniqueAttachments(stoppage.attachments, MAX_ALLOWED_ATTACHMENTS)

  const departmentLabel =
    stoppage.upmDepartmentId && departments.find(({id}) => id === stoppage.upmDepartmentId)?.label

  return (
    <CardBox data-test-id="stoppage-details">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>{renderTitle(hiddenFields?.title, stoppage.title)}</Box>

        <StoppageDetailsActions
          stoppage={stoppage}
          timezone={timezone}
          onEdit={onEdit}
          onDelete={onDelete}
          onSplit={onSplit}
          onClose={onClose}
          isEditStoppageEnabled={isEditStoppageEnabled}
          isSplitStoppageEnabled={isSplitStoppageEnabled}
          isDeleteStoppageEnabled={isDeleteStoppageEnabled}
          isStoppageSubmitSupported={isStoppageSubmitSupported}
        />
      </Box>

      {isStoppageSubmitSupported && (
        <SubmitSection
          onSubmit={
            onSubmit
              ? () => {
                  void submitStoppage(stoppage.id)
                }
              : undefined
          }
          isLoading={isSubmitting}
          isSubmitted={stoppage.isSubmitted}
        />
      )}

      <Grid container mt={0} mb={1.5} spacing={2}>
        <Grid item xs={12}>
          <Tag label={t('shiftEvent.label.stoppage')}>
            <EventTypeLabel eventType={stoppage.eventType} />
          </Tag>
        </Grid>
        <Grid item xs={12}>
          <Tag label={t('shiftEvent.label.stoppageType')} data-test-id="stoppage-details-type">
            {t(STOPPAGE_TYPE_TRANSLATION_KEYS[stoppage.stoppageType])}
          </Tag>
        </Grid>
        {!hideSalesImpact && (
          <Grid item {...regularSize}>
            <Tag
              label={t('shiftEvent.label.salesImpact')}
              data-test-id="stoppage-details-sales-impact"
            >
              <SalesImpactLabel hasSalesImpact={stoppage.hasSalesImpact} />
            </Tag>
          </Grid>
        )}
        {/* HCP-42934 uncomment when management decides*/}
        {/* <Grid item {...regularSize}>*/}
        {/*  <Tag labelKey="shiftEvent.label.cost" data-test-id="stoppage-details-cost">*/}
        {/*    {stoppage.cost ? `${stoppage.cost} ${stoppage.costCurrency}` : '-'}*/}
        {/*  </Tag>*/}
        {/* </Grid>*/}
        <Grid item {...regularSize}>
          <Tag label={t('shiftEvent.label.stoppageCode')} data-test-id="stoppage-details-code">
            {'stoppageCode' in stoppage && shifthandoverConfig
              ? `${stoppage.stoppageCode} ${getStoppageDescription(
                  mapStoppage(stoppage, timezone),
                  {
                    kiln: shifthandoverConfig.kilnStoppageCodes,
                    cementMill: shifthandoverConfig.finishMillStoppageCodes,
                    rawMill: shifthandoverConfig.rawMillStoppageCodes,
                    alternativeFuelInstallation:
                      shifthandoverConfig.alternativeFuelInstallationStoppageCodes
                  }
                )}`
              : '-'}
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={t('shiftEvent.label.timeframe')} data-test-id="stoppage-details-timeframe">
            <DateTimePeriod
              start={stoppage.stoppageStart}
              end={stoppage.stoppageEnd}
              timezone={timezone}
            />
          </Tag>
        </Grid>
        <Grid item {...regularSize}>
          <Tag label={t('shiftEvent.label.duration')} data-test-id="stoppage-details-duration">
            {getStoppageDuration(stoppage.stoppageStart, stoppage.stoppageEnd)}
          </Tag>
        </Grid>
        {isStoppageTypeWithReason(stoppage.stoppageType) && 'stoppageReason' in stoppage && (
          <Grid item xs={12}>
            <Tag
              label={t('shiftEvent.label.stoppageReason')}
              data-test-id="event-detail-stoppageReason"
            >
              {stoppage.stoppageReason}
            </Tag>
          </Grid>
        )}
        {departmentLabel && (
          <Grid item xs={12}>
            <Tag
              data-test-id="event-detail-stopped-main-equipment"
              label={t('shiftEvent.label.stoppedMainEquipment')}
            >
              {departmentLabel}
            </Tag>
          </Grid>
        )}
        {renderRootCausingEquipment(hiddenFields, t, stoppage.equipment, stoppage.mainEquipment)}
        {!hiddenFields?.processStage && (
          <Grid item {...regularSize}>
            <Tag
              label={t('shiftEvent.label.processStage')}
              data-test-id="event-detail-processStage"
            >
              {renderProcessStage(stoppage.processStage, data)}
            </Tag>
          </Grid>
        )}
        {renderStoppageDescription(hiddenFields?.description, stoppage.description, t)}

        {stoppage.rootCauseFailureAnalyses && (
          <Grid item xs={12}>
            <Tag
              label={t('shiftEvent.label.rootCauseFailureAnalyses')}
              data-test-id="stoppage-detail-rootCauseFailureAnalyses"
            >
              <Box display="flex" flexDirection="column">
                {stoppage.rootCauseFailureAnalyses.map(({title, id}) => (
                  <Link
                    key={id}
                    href={getMaintainAnalysisUrl(plantId, id)}
                    underline="none"
                    fontWeight={600}
                    width="fit-content"
                  >
                    {title}
                  </Link>
                ))}
              </Box>
            </Tag>
          </Grid>
        )}
      </Grid>

      {!hideAttachments && (
        <>
          <CardSectionTitle sx={headlineSx()}>
            {t('shiftEvent.attachments')} {`${uniqueAttachments.length}/${MAX_ALLOWED_ATTACHMENTS}`}
          </CardSectionTitle>
          <Divider sx={{borderColor: 'divider'}} />
          <Box component={'span'} sx={imageBoxSx()}>
            {uniqueAttachments.map((attachment, index) => (
              <AttachmentItem
                key={`attachment-${attachment.id}`}
                attachment={attachment}
                setActiveAttachmentId={setActiveAttachmentId}
                disableError={typeof activeAttachmentId === 'number' || !!index}
              />
            ))}
          </Box>
        </>
      )}

      {typeof activeAttachmentId === 'number' && (
        <AttachmentItemsDisplay
          attachments={uniqueAttachments}
          activeAttachmentId={activeAttachmentId}
          onClose={() => setActiveAttachmentId(undefined)}
        />
      )}
    </CardBox>
  )
}
